import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface Topic {
  id: string;
  title: string;
  content: string;
  slug: string;
  user_id: string | null;
  category_id: string;
  view_count: number;
  reply_count: number;
  is_pinned: boolean;
  is_locked: boolean;
  created_at: string;
  updated_at: string;
  last_post_at: string;
  categories?: {
    name: string;
    color: string;
    slug: string;
  };
  profiles?: {
    display_name: string;
    avatar_url: string | null;
  };
}

interface UseTopicsOptions {
  categoryId?: string;
  page?: number;
  limit?: number;
  sortBy?: 'latest' | 'popular' | 'oldest' | 'newest';
}

export const useTopics = (options: UseTopicsOptions = {}) => {
  const { categoryId, page = 1, limit = 20, sortBy = 'latest' } = options;
  const offset = (page - 1) * limit;

  return useQuery({
    queryKey: ['topics', categoryId, page, limit, sortBy],
    queryFn: async (): Promise<{ topics: Topic[]; total: number }> => {
      let query = supabase
        .from('topics')
        .select(`
          *,
          categories (
            name, 
            color, 
            slug,
            parent_category:parent_category_id(
              slug,
              parent_category:parent_category_id(slug)
            )
          )
        `, { count: 'exact' });

      if (categoryId) {
        query = query.eq('category_id', categoryId);
      }

      // Sorting
      query = query.order('is_pinned', { ascending: false });
      
      switch (sortBy) {
        case 'popular':
          query = query.order('view_count', { ascending: false });
          break;
        case 'oldest':
          query = query.order('created_at', { ascending: true });
          break;
        case 'newest':
          query = query.order('created_at', { ascending: false });
          break;
        case 'latest':
        default:
          query = query.order('last_post_at', { ascending: false });
      }

      query = query.range(offset, offset + limit - 1);

      const { data: topics, error, count } = await query;

      if (error) throw error;

      // Enrich with profiles
      const userIds = [...new Set(topics?.map(t => t.user_id).filter(Boolean))] as string[];
      let profilesMap = new Map();

      if (userIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, display_name, avatar_url')
          .in('id', userIds);

        profiles?.forEach(profile => {
          profilesMap.set(profile.id, profile);
        });
      }

      const enrichedTopics = topics?.map(topic => ({
        ...topic,
        profiles: topic.user_id ? profilesMap.get(topic.user_id) : null
      })) || [];

      return {
        topics: enrichedTopics,
        total: count || 0
      };
    },
  });
};
